import { createSelector } from 'reselect'
import { sortBy } from 'lodash'

import { getBadgeFileType, isDataFile } from './utils'

const getFilesState = state => state.files.files

export const isFetchingFilesList = createSelector(
  getFilesState,
  filesState => filesState.isFetching
)
export const hasErrorFetchingFilesList = createSelector(
  getFilesState,
  filesState => Boolean(filesState.error)
)
export const getFilesList = createSelector(
  isFetchingFilesList,
  hasErrorFetchingFilesList,
  getFilesState,
  (isFetching, hasError, filesState) => {
    if (isFetching || hasError) return []
    return filesState.data
  }
)

export const getHasUncleanDataFile = createSelector(
  getFilesList,
  files => files.some(file => file.fileType === 'uncleaned_data')
)

export const getDocuments = createSelector(
  getFilesList,
  files => files.filter(file => !['data', 'uncleaned_data', 'user_data'].includes(file.fileType))
)

export const getFilesFilterQuery = state => state.files.files.filterQuery ?? ''
export const getSortColumn = state => state.files.files.sorting.column
export const getSortOrder = state => state.files.files.sorting.order

export const getFileCards = createSelector(
  getDocuments,
  getFilesFilterQuery,
  getSortColumn,
  getSortOrder,
  (files, filterQuery, sortColumn, sortOrder) => {
    const query = filterQuery.toLocaleLowerCase()
    const searchedFiles = files.filter(file => (!query || file.name.toLocaleLowerCase().includes(query)))
    const sortedFiles = sortBy(searchedFiles, [file => file[sortColumn]]) || []

    if (sortOrder === 'descending') return sortedFiles.reverse()
    return sortedFiles
  }
)

const getStagingState = state => state.files.staging

const getExistingFilesMap = createSelector(
  getFilesList,
  files => files.reduce((acc, file) => {
    acc[file.name] = true
    return acc
  }, {})
)

export const getStagedFiles = createSelector(
  getExistingFilesMap,
  getStagingState,
  (
    existingFiles,
    {
      files,
      groupRestrictions,
      dataFileNeedsCleaning,
      isSearchable,
      isUploading,
      isComplete,
      errors,
    }
  ) => files.map(file => ({
    file,
    type: getBadgeFileType(file.name),
    groupRestrictions: groupRestrictions[file.name],
    needsCleaning: dataFileNeedsCleaning[file.name] ?? false,
    isSearchable: isSearchable[file.name] ?? false,
    isUploading: isUploading[file.name] ?? false,
    isComplete: isComplete[file.name] ?? false,
    uploadError: errors[file.name] ?? null,
    willReplaceExistingFile: existingFiles[file.name] ?? false,
  }))
)

export const getIsUploadingStagedFiles = createSelector(
  getStagingState,
  ({ files, isUploading }) => files.some(file => isUploading[file.name] ?? false)
)

export const getHasFileUploadFailed = createSelector(
  getStagingState,
  ({ files, errors }) => files.some(file => errors[file.name] ?? false)
)

const newFileState = state => state.files.newFile

export const getNewFile = createSelector([newFileState], newFile => newFile)

const updateFileState = state => state.files.updateFile

export const getUpdateFile = createSelector([updateFileState], updateFile => updateFile)

const deleteFileState = state => state.files.deleteFile

export const getDeleteFile = createSelector(deleteFileState, deleteFile => deleteFile)

export const getIsDeletingFile = createSelector(
  deleteFileState,
  state => state.isFetching
)

export const getHasErrorDeletingFile = createSelector(
  deleteFileState,
  state => Boolean(state.error),
)

const currentDocumentState = state => state.files.currentDocument

export const getCurrentDocument = createSelector(
  [currentDocumentState],
  currentDocument => currentDocument
)

const fileDetails = state => state.files.fileDetails

export const getIsFileDetailsLoading = state => fileDetails(state).isLoading
export const getHasErrorLoadingFileDetails = state => Boolean(fileDetails(state).error)
export const getFileDetails = state => fileDetails(state).data ?? {}

export const getStagedDataFile = createSelector(
  getStagedFiles,
  stagedFiles => stagedFiles.filter(file => isDataFile(file.file.name)) || []
)

export const getFileIntervalId = state => state.files.files.fileIntervalId

export const getIsPollingForFiles = state => Boolean(getFileIntervalId(state))

export const getFilePreviewsComplete = createSelector(
  getDocuments,
  files => !files.some(file => file.previewState === 'processing' || file.previewState === 'initialized')
)
